import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  TrendingUp,
  Users,
  ChevronRight,
  Flame,
  Crown,
  Award,
  Loader2
} from 'lucide-react';
import { cn } from '@/utils/cn';
import {
  getTrendingDecisions,
  getPopularGroups,
  getTopContributors
} from '@/services';

const categoryColors: Record<string, { bg: string; text: string }> = {
  career: { bg: '#6366F1', text: '#4F46E5' },
  finance: { bg: '#10B981', text: '#059669' },
  health: { bg: '#F59E0B', text: '#D97706' },
  lifestyle: { bg: '#14B8A6', text: '#0D9488' },
  technology: { bg: '#3B82F6', text: '#1D4ED8' },
  'real-estate': { bg: '#EC4899', text: '#DB2777' },
  education: { bg: '#8B5CF6', text: '#7C3AED' },
};

export function RightSidebar() {
  const { data: trendingDecisions = [], isLoading: trendingLoading } = useQuery({
    queryKey: ['trending-decisions-sidebar'],
    queryFn: () => getTrendingDecisions({ limit: 3 }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const { data: suggestedGroups = [], isLoading: groupsLoading } = useQuery({
    queryKey: ['popular-groups-sidebar'],
    queryFn: () => getPopularGroups({ limit: 3 }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  const { data: topContributors = [], isLoading: contributorsLoading } = useQuery({
    queryKey: ['top-contributors-sidebar'],
    queryFn: () => getTopContributors({ limit: 3 }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  return (
    <aside className="right-sidebar">
      <div className="p-4 space-y-4">
        {/* Trending Decisions - Orange/Amber Theme */}
        <section className="right-sidebar__section right-sidebar__section--trending">
          <div className="right-sidebar__section-header">
            <div className="right-sidebar__section-icon bg-amber-500/20">
              <Flame className="w-5 h-5 text-amber-600" />
            </div>
            <h3 className="right-sidebar__section-title">Trending Now</h3>
          </div>

          <div className="space-y-3">
            {trendingLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 text-amber-500 animate-spin" />
              </div>
            ) : trendingDecisions.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No trending decisions yet</p>
            ) : (
              trendingDecisions.map((decision, index) => (
                <Link
                  key={decision.id}
                  to={`/decision/${decision.id}`}
                  className="block group"
                >
                  <div className="flex items-start gap-3">
                    <span className="text-lg font-bold text-amber-500/70 w-5">
                      {index + 1}
                    </span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 group-hover:text-amber-700 transition-colors line-clamp-2">
                        {decision.title}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span
                          className="text-xs px-2 py-0.5 rounded-full font-medium"
                          style={{
                            backgroundColor: `${categoryColors[decision.category?.slug]?.bg || '#6366F1'}20`,
                            color: categoryColors[decision.category?.slug]?.text || '#4F46E5'
                          }}
                        >
                          {decision.category?.name || 'Uncategorized'}
                        </span>
                        <span className="text-xs text-slate-500">
                          {decision.totalVotes || 0} votes
                        </span>
                      </div>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>

          <Link
            to="/explore"
            className="flex items-center justify-center gap-1 mt-4 pt-3 border-t border-amber-200/50 text-sm text-amber-700 font-medium hover:underline"
          >
            See more
            <ChevronRight className="w-4 h-4" />
          </Link>
        </section>

        {/* Suggested Groups - Purple/Violet Theme */}
        <section className="right-sidebar__section right-sidebar__section--groups">
          <div className="right-sidebar__section-header">
            <div className="right-sidebar__section-icon bg-violet-500/20">
              <Users className="w-5 h-5 text-violet-600" />
            </div>
            <h3 className="right-sidebar__section-title">Groups for You</h3>
          </div>

          <div className="space-y-2">
            {groupsLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 text-violet-500 animate-spin" />
              </div>
            ) : suggestedGroups.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No groups available yet</p>
            ) : (
              suggestedGroups.map((group) => (
                <div
                  key={group.id}
                  className="flex items-center gap-3 p-2 -mx-2 rounded-xl hover:bg-violet-100/50 transition-colors"
                >
                  <Link
                    to={`/groups/${group.slug || group.id}`}
                    className="flex-1 flex items-center gap-3"
                  >
                    <div className="w-10 h-10 rounded-xl bg-white shadow-sm flex items-center justify-center text-xl">
                      {group.icon || '👥'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-slate-800 truncate text-sm">
                        {group.name}
                      </p>
                      <p className="text-xs text-slate-500">
                        {(group.membersCount || 0).toLocaleString()} members
                      </p>
                    </div>
                  </Link>
                  <Link
                    to={`/groups/${group.id}`}
                    className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors shadow-sm ${group.isMember
                      ? 'text-slate-600 bg-slate-100 hover:bg-slate-200'
                      : group.isPending
                        ? 'text-amber-600 bg-amber-50 hover:bg-amber-100'
                        : 'text-violet-600 bg-white hover:bg-violet-50'
                      }`}
                  >
                    {group.isMember ? 'View' : group.isPending ? 'Pending' : 'Join'}
                  </Link>
                </div>
              ))
            )}
          </div>

          <Link
            to="/groups"
            className="flex items-center justify-center gap-1 mt-4 pt-3 border-t border-violet-200/50 text-sm text-violet-700 font-medium hover:underline"
          >
            Discover groups
            <ChevronRight className="w-4 h-4" />
          </Link>
        </section>

        {/* Top Contributors - Green/Emerald Theme */}
        <section className="right-sidebar__section right-sidebar__section--contributors">
          <div className="right-sidebar__section-header">
            <div className="right-sidebar__section-icon bg-emerald-500/20">
              <TrendingUp className="w-5 h-5 text-emerald-600" />
            </div>
            <h3 className="right-sidebar__section-title">Top Contributors</h3>
          </div>

          <div className="space-y-2">
            {contributorsLoading ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-5 h-5 text-emerald-500 animate-spin" />
              </div>
            ) : topContributors.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No contributors yet</p>
            ) : (
              topContributors.map((user, index) => (
                <Link
                  key={user.id}
                  to={`/profile/${user.username}`}
                  className="flex items-center gap-3 p-2 -mx-2 rounded-xl hover:bg-emerald-100/50 transition-colors"
                >
                  <div className="relative">
                    <img
                      src={user.avatar || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.id}`}
                      alt={user.displayName || user.username}
                      className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow-sm"
                    />
                    {index <= 2 && (
                      <div className={cn(
                        'absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center shadow',
                        index === 0 && 'bg-amber-400',
                        index === 1 && 'bg-slate-300',
                        index === 2 && 'bg-amber-600'
                      )}>
                        {index === 0 ? (
                          <Crown className="w-3 h-3 text-white" />
                        ) : (
                          <Award className="w-3 h-3 text-white" />
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-slate-800 truncate text-sm">
                      {user.displayName || user.username}
                    </p>
                    <p className="text-xs text-slate-500">
                      @{user.username}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-bold text-emerald-600">
                      {(user.impactScore || 0).toLocaleString()}
                    </p>
                    <p className="text-xs text-slate-500">points</p>
                  </div>
                </Link>
              ))
            )}
          </div>

          <Link
            to="/leaderboard"
            className="flex items-center justify-center gap-1 mt-4 pt-3 border-t border-emerald-200/50 text-sm text-emerald-700 font-medium hover:underline"
          >
            View leaderboard
            <ChevronRight className="w-4 h-4" />
          </Link>
        </section>

        {/* Footer */}
        <div className="text-center py-4">

          {/* App links */}
          <div className="mt-2 text-center">
            <p className="text-xs text-slate-400 mb-1">
              Get the Decixa app
            </p>
            <div className="flex items-center justify-center gap-2 text-xs">
              <a
                href="https://play.google.com/store/apps/details?id=com.decixa.app"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                Google Play
              </a>

              <span className="text-slate-300">|</span>

              <a
                href="https://apps.apple.com/in/app/decixa/id6760898576"
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-400 hover:text-slate-600 transition-colors"
              >
                App Store
              </a>
            </div>
          </div>
          <div className="flex items-center justify-center gap-4 mt-4">
            <Link to="/terms-and-conditions" className="text-xs text-slate-400 hover:text-slate-600">Terms</Link>
            <Link to="/privacy" className="text-xs text-slate-400 hover:text-slate-600">Privacy</Link>
            {/* <Link to="/help" className="text-xs text-slate-400 hover:text-slate-600">Help</Link> */}
          </div>
          <p className="text-xs text-slate-400">
            © 2025 Decixa. All rights reserved.
          </p>
        </div>
      </div>
    </aside>
  );
}
